import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, Download, FileText, Search, FileSignature, Send, Copy } from 'lucide-react';
import { letters, matters as mattersApi, API, esign } from '@/lib/api';
import { toast } from 'sonner';
import { safeHtml } from '@/lib/sanitize';

export default function LettersPage() {
  const [templates, setTemplates] = React.useState([]);
  const [matters, setMatters] = React.useState([]);
  const [selectedTpl, setSelectedTpl] = React.useState(null);
  const [selectedMatter, setSelectedMatter] = React.useState('');
  const [rendered, setRendered] = React.useState(null);
  const [rendering, setRendering] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [catFilter, setCatFilter] = React.useState('all');
  const [esignBusy, setEsignBusy] = React.useState(false);
  const [esignEmail, setEsignEmail] = React.useState('');
  const [esignResult, setEsignResult] = React.useState(null);

  // Auto-fill signer email when a matter is picked
  React.useEffect(() => {
    if (!selectedMatter) { setEsignEmail(''); return; }
    const m = matters.find((x) => x.ref === selectedMatter);
    setEsignEmail(m?.applicant_email || '');
    setEsignResult(null);
  }, [selectedMatter, matters]);

  React.useEffect(() => {
    letters.templates().then(setTemplates);
    mattersApi.list().then(setMatters).catch(() => setMatters([]));
  }, []);

  const categories = Array.from(new Set(templates.map((t) => t.category))).sort();
  const filtered = templates.filter((t) => {
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const doRender = async () => {
    if (!selectedTpl || !selectedMatter) return;
    setRendering(true); setRendered(null);
    try {
      const r = await letters.render(selectedTpl.id, selectedMatter);
      setRendered(r);
    } finally { setRendering(false); }
  };

  const downloadPdf = async () => {
    if (!selectedTpl || !selectedMatter) return;
    setPdfLoading(true);
    try {
      const blob = await API.post('/letters/render/pdf',
        { template_id: selectedTpl.id, matter_ref: selectedMatter },
        { responseType: 'blob' }).then((r) => r.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTpl.title}-${selectedMatter}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setPdfLoading(false); }
  };

  const sendForEsign = async () => {
    if (!rendered || !selectedMatter || !esignEmail.trim()) return;
    setEsignBusy(true); setEsignResult(null);
    try {
      const r = await esign.create({
        matter_ref: selectedMatter,
        title: rendered.title || selectedTpl?.title || 'Document',
        body_html: rendered.html,
        signer_email: esignEmail.trim(),
        template_id: selectedTpl?.id || null,
      });
      setEsignResult(r);
      if (r.signer_link?.sent) toast.success('eSignature request emailed');
      else toast.info('Request created — copy the link below to share manually');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setEsignBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl p-8" data-testid="letters-page">
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6 text-[#2ECC9A]" />
        <h1 className="text-2xl font-serif font-bold text-[#1A3A5C]">Letters library</h1>
        <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">
          {templates.length} templates
        </Badge>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Australian migration letter templates with token substitution from matter + firm data.
        Render to inline HTML or export a firm-branded PDF via WeasyPrint.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* Template picker */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Search className="h-3 w-3 text-slate-400" />
              <Input data-testid="letters-search" placeholder="Search templates…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="h-8 text-xs" />
            </div>
            <div className="mt-2">
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger data-testid="letters-category" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-3 max-h-[560px] space-y-1 overflow-auto pr-1">
              {filtered.map((t) => (
                <button key={t.id}
                        data-testid={`template-${t.id}`}
                        onClick={() => { setSelectedTpl(t); setRendered(null); }}
                        className={`w-full rounded-md border p-3 text-left transition ${
                          selectedTpl?.id === t.id
                            ? 'border-[#2ECC9A] bg-[#2ECC9A]/5'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}>
                  <div className="text-xs font-semibold text-[#1A3A5C]">{t.title}</div>
                  <div className="mt-1 flex items-center gap-1">
                    <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500">
                      {t.category}
                    </Badge>
                    {!t.seeded && <Badge className="bg-[#2ECC9A]/10 text-[#0F766E] border-[#2ECC9A]/30 text-[9px]">Custom</Badge>}
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <div className="py-8 text-center text-xs text-slate-400">No templates match</div>}
            </div>
          </CardContent>
        </Card>

        {/* Renderer */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            {!selectedTpl && (
              <div className="flex h-full min-h-[400px] items-center justify-center text-sm text-slate-400">
                <FileText className="mr-2 h-4 w-4" /> Select a template to preview & render
              </div>
            )}
            {selectedTpl && (
              <>
                <div className="text-lg font-semibold text-[#1A3A5C]">{selectedTpl.title}</div>
                <div className="text-xs text-slate-500">{selectedTpl.category} · {selectedTpl.seeded ? 'Seed template' : 'Firm custom'}</div>

                <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500">Matter reference</label>
                    <Select value={selectedMatter} onValueChange={setSelectedMatter}>
                      <SelectTrigger data-testid="letters-matter" className="mt-1 h-9 text-xs"><SelectValue placeholder="Select a matter…" /></SelectTrigger>
                      <SelectContent>
                        {matters.map((m) => (
                          <SelectItem key={m.ref} value={m.ref}>{m.ref} · {m.applicant} · {m.subclass}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="letters-render" onClick={doRender} disabled={!selectedMatter || rendering}
                          className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                    {rendering ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Render preview'}
                  </Button>
                  <Button data-testid="letters-download-pdf" onClick={downloadPdf}
                          disabled={!selectedMatter || pdfLoading}
                          variant="outline"
                          className="h-9 gap-1 border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[#0F766E] text-xs">
                    {pdfLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Download className="h-3 w-3" /> Download PDF</>}
                  </Button>
                </div>

                {rendered && (
                  <>
                    <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-500">Tokens filled</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {rendered.tokens_used.map((tk) => (
                        <Badge key={tk} variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700 font-mono">{tk}</Badge>
                      ))}
                    </div>
                    <div className="mt-4 rounded-md border border-slate-200 bg-white p-6 shadow-inner"
                         data-testid="letters-preview-html"
                         dangerouslySetInnerHTML={{ __html: safeHtml(rendered.html) }} />

                    <div className="mt-4 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-3"
                         data-testid="letters-esign-card">
                      <div className="flex items-center gap-2 text-xs font-semibold text-[#1A3A5C]">
                        <FileSignature className="h-3.5 w-3.5 text-[#2ECC9A]" /> Send this letter for eSignature
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        Client gets a magic link, signs with typed name + drawn signature; the audit certificate lands in Documents automatically.
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          data-testid="letters-esign-email"
                          type="email"
                          value={esignEmail}
                          onChange={(e) => setEsignEmail(e.target.value)}
                          placeholder="Signer email"
                          className="h-8 flex-1 min-w-[220px] rounded-md border border-slate-200 bg-white px-2 text-xs"
                        />
                        <Button data-testid="letters-esign-send" onClick={sendForEsign}
                                disabled={esignBusy || !esignEmail.trim()} size="sm"
                                className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                          {esignBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3" /> Send for eSignature</>}
                        </Button>
                      </div>
                      {esignResult && (
                        <div data-testid="letters-esign-result"
                             className={`mt-2 rounded-md p-2 text-[11px] ${
                               esignResult.signer_link?.sent ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'
                             }`}>
                          {esignResult.signer_link?.sent ? (
                            <>Emailed to <b>{esignResult.signer_email}</b>. Track it on the <a className="underline" href="/app/esign">eSign page</a>.</>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="mr-1">Email not delivered — share this link:</span>
                              <code className="flex-1 truncate rounded bg-white px-2 py-1 text-[10px]">{esignResult.signer_link?.link}</code>
                              <Button size="sm" variant="outline" data-testid="letters-esign-copy"
                                      onClick={() => { navigator.clipboard.writeText(esignResult.signer_link?.link || ''); toast.success('Link copied'); }}
                                      className="h-7 gap-1 border-slate-200 text-[10px]">
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
